import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, PC_LOGO_URL } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Btn, Card, Input } from '@/components/ui-ios';
import {
  Truck, Package, Search as SearchIcon, LogOut, RefreshCw, ChevronRight,
  Camera, QrCode, Zap, ScanLine, Flag, Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import { isRuggedScannerDevice } from '@/lib/deviceDetect';
import { useHardwareScanner, parseScannerInput } from '@/hooks/useHardwareScanner';
import { usePortraitLock } from '@/hooks/usePortraitLock';

// Heavy components only needed AFTER user interaction (open scanner, open
// detail, tap signature) are loaded on demand. On a TC27 over 4G this
// removes ~80-120 KB of JS from the initial parse/compile.
const QrScannerOverlay = lazy(() => import('@/components/portalettere/QrScannerOverlay'));
const RaccomandataDetail = lazy(() => import('@/components/portalettere/RaccomandataDetail'));

const SCAN_PREF_KEY = 'pc_scan_mode'; // 'camera' | 'laser'
// LocalStorage key for the last-known "today" list. Hydrating from here
// gives the operator an instant first paint while the network request is
// still in-flight (stale-while-revalidate pattern).
const TODAY_CACHE_KEY = 'pc_m_today_cache_v1';

/**
 * Memoized list row — extracted so React only re-renders the row that
 * actually changed (e.g. when only the "today" length changes by 1, we
 * don't re-render every existing row).
 */
const TodayRow = React.memo(function TodayRow({ r, onOpen }) {
  const dest = r.destinatario || {};
  // /m/today merges raccomandate + posta massiva — entity_type discriminates.
  // We show a small "POSTA MASSIVA" pill on PM items so the postman knows
  // the row will skip the firma flow on delivery.
  const isPM = r.entity_type === 'posta_massiva';
  return (
    <li>
      <button onClick={() => onOpen(r)} className="w-full text-left px-4 py-3 hover:bg-[color:var(--surface-2)] active:bg-zinc-100 transition-colors flex items-start gap-3" data-testid={`m-today-item-${r.id}`}>
        <div className={`h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 ${isPM ? 'bg-sky-50' : 'bg-orange-50'}`}>
          <Truck className={`h-5 w-5 ${isPM ? 'text-sky-600' : 'text-[color:var(--brand-orange)]'}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate flex items-center gap-1.5">
            <span className="truncate">{dest.nome || '—'}</span>
            {isPM && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200 tracking-wider shrink-0">PM</span>}
          </div>
          <div className="text-[11px] text-[color:var(--text-secondary)] truncate">{[dest.indirizzo, dest.civico].filter(Boolean).join(' ')} · {dest.cap} {dest.citta}</div>
          <div className="text-[11px] font-mono mt-0.5 text-zinc-700">{r.tracking_code}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-zinc-400 shrink-0 mt-3" />
      </button>
    </li>
  );
});

/**
 * Mobile-first page for the postman ("portalettere") flow.
 *
 * Two main areas (mutually exclusive):
 *  - Listing view: today's deliveries + manual lookup + "Metti in consegna"
 *    pickup CTA. Pickup uses either the camera (QR overlay) or the rugged
 *    device's hardware laser (DataWedge / HID keystroke scanner) depending
 *    on the chosen scan mode.
 *  - Detail view: the selected raccomandata's destinatario, current state,
 *    quick state-update tiles, optional digital signature, full timeline.
 *
 * Scan mode is auto-detected from the user-agent (Zebra TC27/TC52 default
 * to laser, browsers/smartphones default to camera) and remembered per
 * device in localStorage. The operator can flip it at any time via the
 * pill toggle in the header.
 *
 * Heavy components live in @/components/portalettere/* to keep this page
 * focused on orchestration (state, navigation, scan modes, branding).
 */
export default function PortaLetterePage() {
  // Force portrait orientation while the postman is in /m. Rugged Android
  // handhelds tend to rotate aggressively even when the OS rotation lock
  // is enabled — the Screen Orientation API gives us a hard lock on
  // browsers that support it, and the CSS overlay below catches the
  // residual landscape state.
  usePortraitLock();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [code, setCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [detail, setDetail] = useState(null);
  // Hydrate "today" list from localStorage IMMEDIATELY on first render — this
  // gives the operator an instant first paint with the previous session's
  // raccomandate, while the network refresh runs in the background. On a
  // TC27 over 4G this turns a ~600-1000ms "loading…" flash into 0ms.
  const [today, setToday] = useState(() => {
    try {
      const cached = localStorage.getItem(TODAY_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) { /* private mode / corrupt cache */ }
    return [];
  });
  const [loadingToday, setLoadingToday] = useState(false);

  // ---- scan mode (camera vs laser) ----
  // Auto-detect on first load: ruggedized scanner device → laser by default,
  // everything else → camera. Persisted in localStorage so the choice
  // survives reloads on every individual device.
  const [scanMode, setScanMode] = useState(() => {
    try {
      const saved = localStorage.getItem(SCAN_PREF_KEY);
      if (saved === 'camera' || saved === 'laser') return saved;
    } catch (e) { /* private mode */ }
    return isRuggedScannerDevice() ? 'laser' : 'camera';
  });
  useEffect(() => {
    try { localStorage.setItem(SCAN_PREF_KEY, scanMode); } catch (e) { /* private mode */ }
  }, [scanMode]);

  // ---- camera overlay state ----
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerMode, setScannerMode] = useState('lookup'); // 'lookup' | 'pickup'
  const [scanFeedback, setScanFeedback] = useState(null); // {tone, title, message}
  const [pickupCount, setPickupCount] = useState(0);

  // ---- laser mode state ----
  // When in laser pickup mode we show a "ready to scan" panel and listen
  // globally for hardware keystrokes; each successful scan increments the
  // counter and shows feedback inline, just like the continuous camera mode.
  const [laserSession, setLaserSession] = useState(null); // 'lookup' | 'pickup' | null
  const [laserFeedback, setLaserFeedback] = useState(null);
  // Real-time progress buffer: what the hardware scanner is currently
  // sending. We show it on screen so the field operator can immediately
  // see "yes the scanner IS sending" even if the upstream parser/network
  // call later fails. Critical for diagnosing DataWedge config issues on
  // a TC27 without dev tools.
  const [laserBuffer, setLaserBuffer] = useState('');
  // Tracks whether the hidden capture input currently has focus. We show
  // a "Tocca per attivare scanner" overlay button when it doesn't, so the
  // operator can recover with a single tap if the WebView dropped focus.
  const [laserInputFocused, setLaserInputFocused] = useState(false);
  // Hidden <input> auto-focused while the laser overlay is open. DataWedge's
  // "Keystroke output" mode injects scanned chars into the currently focused
  // element; without this hidden input the focus would be on <body>, which
  // is a less reliable target across Android WebView versions. With it,
  // every char from the scanner reliably ends up here and our window-level
  // `useHardwareScanner` listener mirrors them for parsing.
  const laserInputRef = useRef(null);

  const loadToday = useCallback(async () => {
    setLoadingToday(true);
    try {
      const { data } = await api.get('/m/today');
      const list = data || [];
      setToday(list);
      // Persist for next cold start (stale-while-revalidate).
      try { localStorage.setItem(TODAY_CACHE_KEY, JSON.stringify(list)); } catch (e) { /* private mode */ }
    } catch (e) {
      // Network may be flaky on field; user can refresh. We log so that
      // recurring failures (DNS / 5xx) show up in the device console
      // during support sessions, but we don't toast (would spam the
      // portalettere every refresh attempt).
      console.warn('loadToday failed:', e?.message || e);
    }
    finally { setLoadingToday(false); }
  }, []);

  useEffect(() => { loadToday(); }, [loadToday]);

  // Posta Massiva has a separate lifecycle (same esiti as raccomandate but
  // NO firma digitale) and its own endpoints. We detect M-prefixed codes
  // only for the PICKUP action (which routes to /m/posta-massiva/pickup);
  // all other interactions (lookup, deliver, esiti) happen through the
  // RaccomandataDetail screen which normalizes both entity types.
  const isPostaMassivaCode = (raw) => /^M\d+/i.test((raw || '').trim());

  const lookup = useCallback(async (raw) => {
    const c = (raw || '').trim();
    if (!c) { toast.error('Inserisci un codice'); return; }
    setSearching(true);
    try {
      const { data } = await api.get('/m/lookup', { params: { code: c } });
      // Both entity types open the same detail screen — RaccomandataDetail
      // normalizes the shape internally (raccomandata vs posta_massiva) and
      // skips the signature pad for PM.
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Codice non trovato');
    } finally { setSearching(false); }
  }, []);

  const reloadDetail = async () => {
    if (!detail) return;
    try {
      const target = detail.raccomandata || detail.posta_massiva;
      if (!target) return;
      const { data } = await api.get('/m/lookup', { params: { code: target.tracking_code || target.id } });
      setDetail(data);
      loadToday();
    } catch (e) {
      // Lookup refresh failed — keep the current detail open so the
      // operator doesn't lose context. Logged for support diagnosis.
      console.warn('reloadDetail failed:', e?.message || e);
    }
  };

  const openItem = useCallback(async (r) => { await lookup(r.tracking_code || r.id); }, [lookup]);

  // ---- camera result handlers (all routed through the smart handler) ----
  const onLookupScanResult = async (raw) => {
    // Single-shot lookup mode: close the camera, run the smart router.
    setScannerOpen(false);
    if (raw) {
      setCode(raw);
      const fb = await handlePickupCode(raw);
      if (fb && fb.tone === 'error') toast.error(fb.message || fb.title);
    }
  };

  // Smart scan: every scanned code is first looked up; based on its current
  // esito we decide whether to PICK IT UP (not yet in delivery) or OPEN the
  // DETAIL (already in delivery → operator wants to esitare it). This
  // collapses the old separate "Metti in consegna" / "Esita" actions into
  // a single mental model: "scansiona e basta".
  //
  // The function is named `handlePickupCode` for backwards compatibility
  // with the callers — it's the unified scan entry point now.
  const handlePickupCode = useCallback(async (raw) => {
    const c = (raw || '').trim();
    if (!c) return null;
    // Final outcomes — the item is closed, just show it for reference.
    const FINAL_ESITI = new Set([
      'CONSEGNATA', 'LASCIATO_AVVISO', 'RIFIUTATA', 'TRASFERITO',
      'SCONOSCIUTO_CIVICO', 'INDIRIZZO_ERRATO', 'DECEDUTO', 'RITORNO_MITTENTE',
    ]);
    // Pre-pickup states: item is still on the desk / freshly accepted.
    const PICKUP_ESITI = new Set(['', 'CREATA', 'PRESA_IN_CARICO']);
    try {
      const { data } = await api.get('/m/lookup', { params: { code: c } });
      const isPM = data?.entity_type === 'posta_massiva' || !!data?.posta_massiva;
      const item = isPM ? data.posta_massiva : data.raccomandata;
      const esito = (item?.esito || '').toUpperCase();
      const dest = item?.destinatario || {};

      // Branch A: already settled → just open the detail (operator may still
      // want to override the esito or check the history).
      if (FINAL_ESITI.has(esito)) {
        setLaserSession(null);
        setScannerOpen(false);
        setDetail(data);
        return { tone: 'warning', title: `Già esitata · ${item.tracking_code}`, message: `Stato: ${esito}` };
      }

      // Branch B: still on the desk → pick it up now and stay in scanner
      // mode (continuous pickup is the most common use case).
      if (PICKUP_ESITI.has(esito)) {
        if (isPM) {
          const { data: pm } = await api.post('/m/posta-massiva/pickup', { tracking_code: c });
          if (pm?.already_delivered) {
            return { tone: 'warning', title: 'Già consegnata', message: c };
          }
          setPickupCount(n => n + 1);
          loadToday();
          return { tone: 'success', title: `Posta massiva in consegna · ${c}`, message: dest.nome || '' };
        }
        const { data: r } = await api.post('/m/pickup', { tracking_code: c });
        const dest2 = r?.raccomandata?.destinatario || {};
        const fb = {
          tone: 'success',
          title: `In consegna · ${dest2.nome || c}`,
          message: `${[dest2.indirizzo, dest2.civico].filter(Boolean).join(' ')} · ${dest2.cap || ''} ${dest2.citta || ''}`.trim() || c,
        };
        setPickupCount(n => n + 1);
        loadToday();
        return fb;
      }

      // Branch C: already in delivery (IN_TRANSITO / IN_CONSEGNA) → close
      // the scanner and open the detail so the operator can pick a final
      // esito tile (signature pad for CONSEGNATA on raccomandate).
      setLaserSession(null);
      setScannerOpen(false);
      setDetail(data);
      return { tone: 'success', title: `Esita · ${item.tracking_code}`, message: dest.nome || '' };
    } catch (e) {
      const det = e?.response?.data?.detail || 'Codice non trovato';
      return { tone: 'error', title: 'Errore', message: det };
    }
  }, [loadToday]);

  const onPickupScanResult = async (raw) => {
    const fb = await handlePickupCode(raw);
    if (fb) {
      setScanFeedback(fb);
      setTimeout(() => setScanFeedback(curr => (curr ? null : curr)), 3500);
    }
  };

  const onScanResult = (raw) => {
    if (scannerMode === 'pickup') onPickupScanResult(raw);
    else onLookupScanResult(raw);
  };

  const openLookupScanner = () => {
    if (scanMode === 'laser') { setLaserSession('lookup'); setLaserFeedback(null); return; }
    setScannerMode('lookup'); setScanFeedback(null); setScannerOpen(true);
  };
  const openPickupScanner = () => {
    if (scanMode === 'laser') { setLaserSession('pickup'); setLaserFeedback(null); setPickupCount(0); return; }
    setScannerMode('pickup'); setScanFeedback(null); setPickupCount(0); setScannerOpen(true);
  };
  const closeCameraScanner = () => {
    setScannerOpen(false);
    setScanFeedback(null);
    if (scannerMode === 'pickup' && pickupCount > 0) {
      toast.success(`${pickupCount} raccomandat${pickupCount === 1 ? 'a presa' : 'e prese'} in consegna`);
    }
  };
  const closeLaserSession = () => {
    const mode = laserSession;
    setLaserSession(null);
    setLaserFeedback(null);
    setLaserBuffer('');
    setLaserInputFocused(false);
    if (mode === 'pickup' && pickupCount > 0) {
      toast.success(`${pickupCount} raccomandat${pickupCount === 1 ? 'a presa' : 'e prese'} in consegna`);
    }
  };

  // ---- "Fine Giro" closure handler --------------------------------------
  // Opens the A5 PDF report in a new tab. The operator can then print
  // it via the device's normal print spooler / share menu.
  const [fineGiroBusy, setFineGiroBusy] = useState(false);
  const closeRound = useCallback(async () => {
    if (fineGiroBusy) return;
    if (!window.confirm('Confermi la fine del giro? Verrà aperto il report da stampare.')) return;
    setFineGiroBusy(true);
    try {
      const token = localStorage.getItem('pc_token');
      const resp = await fetch(`${api.defaults.baseURL}/m/fine-giro/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        toast.error('Errore generazione report');
        setFineGiroBusy(false);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success('Report generato');
    } catch (e) {
      toast.error('Errore durante la chiusura del giro');
    } finally {
      setFineGiroBusy(false);
    }
  }, [fineGiroBusy]);
  // We now treat lookup-mode and pickup-mode scans IDENTICALLY: the smart
  // router decides (in handlePickupCode) whether the code needs pickup or
  // opens the detail for esitare. The mode mostly affects the UI (whether
  // the scanner overlay closes after each scan or stays open for bulk
  // pickup).
  const onHardwareScan = useCallback(async (raw) => {
    const fb = await handlePickupCode(raw);
    if (fb) {
      setLaserFeedback(fb);
      setTimeout(() => setLaserFeedback(curr => (curr ? null : curr)), 3500);
    }
    // In single-shot lookup mode the smart router itself closed the
    // overlay (Branch B opens detail; Branch C also opens detail). In
    // pickup mode (Branch A - new code) we stay open for the next scan.
    if (laserSession === 'lookup' && !fb) {
      setLaserSession(null);
      setLaserBuffer('');
    }
  }, [handlePickupCode, laserSession]);
  useHardwareScanner({
    enabled: !!laserSession,
    onScan: onHardwareScan,
    onProgress: setLaserBuffer,
  });

  // Auto-focus the hidden capture input every time the laser overlay opens
  // (and re-grab focus after each scan or feedback change). DataWedge in
  // keystroke mode delivers chars to the focused element; keeping focus on
  // this off-screen input means we never miss a scan, regardless of where
  // the user last touched the screen.
  useEffect(() => {
    if (!laserSession || !laserInputRef.current) return undefined;
    const reFocus = () => { try { laserInputRef.current?.focus({ preventScroll: true }); } catch (e) { /* ignore */ } };
    reFocus();
    // Some WebView builds drop focus on first repaint; re-grab once shortly.
    const t = setTimeout(reFocus, 150);
    return () => clearTimeout(t);
  }, [laserSession, laserFeedback]);

  const onLogout = async () => { await logout(); navigate('/login'); };

  // Multi-tenant branding: agency-specific logo (falls back to PC logo).
  const mBrandingLogo = user?.parent_branding?.logo_data_url || user?.branding?.logo_data_url || '';
  const mLogoSrc = (mBrandingLogo && mBrandingLogo.trim()) || PC_LOGO_URL;
  const mLogoAlt = mBrandingLogo ? 'Logo agenzia' : 'PostaCentrale';

  // Memo the rendered list rows so adding/removing one row doesn't repaint
  // all the others (helpful on rugged TC27 with slow GPU).
  const todayList = useMemo(() => (
    today.map(r => <TodayRow key={r.id} r={r} onOpen={openItem} />)
  ), [today, openItem]);

  return (
    <div className="min-h-screen ios-bg pb-12" data-testid="m-page">
      {/* Landscape fallback overlay — shown when the Screen Orientation API
          lock fails (Safari iOS, Chrome outside fullscreen/PWA). The
          operator gets a clear "ruota in verticale" message instead of a
          broken sideways layout. We use an inline <style> with a media
          query because the overlay must only appear on narrow handheld
          devices that are physically in landscape — Tailwind's `landscape:`
          variant alone would also fire on desktop wide windows. */}
      <style>{`
        @media (orientation: landscape) and (max-height: 600px) {
          .m-landscape-warn { display: flex !important; }
          body { overflow: hidden; }
        }
      `}</style>
      <div
        className="m-landscape-warn hidden fixed inset-0 z-[9999] bg-zinc-900 text-white flex-col items-center justify-center gap-4 p-8"
        style={{ display: 'none' }}
        aria-hidden="true"
        data-testid="m-landscape-warn"
      >
        <div className="text-6xl">📱</div>
        <div className="text-lg font-semibold text-center">Ruota il dispositivo in verticale</div>
        <div className="text-sm opacity-80 text-center max-w-xs">L'app PortaLettere funziona solo in modalità verticale per la massima leggibilità durante la consegna.</div>
      </div>
      {/* Header */}
      <header className="topbar-blur sticky top-0 z-30 border-b border-[color:var(--hairline)]">
        <div className="max-w-md mx-auto px-4 py-2.5 flex items-center gap-3">
          <img src={mLogoSrc} alt={mLogoAlt} className="h-8 w-auto max-w-[120px] object-contain" data-testid="m-brand-logo" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-secondary)] leading-none">PortaLettere</div>
            <div className="text-sm font-semibold tracking-tight truncate">{user?.nome || user?.email}</div>
          </div>
          <Link to="/" className="text-xs text-[color:var(--brand-orange)] font-semibold" data-testid="m-back-desktop" style={{ display: user?.role === 'portalettere' ? 'none' : 'inline-block' }}>Versione PC</Link>
          <button onClick={onLogout} className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-zinc-100 text-zinc-600" title="Logout" data-testid="m-logout"><LogOut className="h-4 w-4" /></button>
        </div>
        {!detail && (
          <div className="max-w-md mx-auto px-4 pb-2.5">
            <div className="inline-flex rounded-full bg-[color:var(--surface-2)] p-0.5 text-xs font-semibold" role="group" aria-label="Modalità scansione">
              <button
                type="button"
                onClick={() => setScanMode('camera')}
                aria-pressed={scanMode === 'camera'}
                className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${scanMode === 'camera' ? 'bg-white shadow text-[color:var(--brand-orange)]' : 'text-[color:var(--text-secondary)] hover:text-zinc-900'}`}
                data-testid="m-scan-mode-camera"
              >
                <Camera className="h-3.5 w-3.5" /> Fotocamera
              </button>
              <button
                type="button"
                onClick={() => setScanMode('laser')}
                aria-pressed={scanMode === 'laser'}
                className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${scanMode === 'laser' ? 'bg-white shadow text-[color:var(--brand-orange)]' : 'text-[color:var(--text-secondary)] hover:text-zinc-900'}`}
                data-testid="m-scan-mode-laser"
              >
                <Zap className="h-3.5 w-3.5" /> Laser
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-md mx-auto px-4 pt-4 space-y-4">
        {detail ? (
          <Suspense fallback={<div className="p-8 text-sm text-[color:var(--text-secondary)]">Caricamento dettaglio…</div>}>
            <RaccomandataDetail
              data={detail}
              onBack={() => { setDetail(null); loadToday(); }}
              onReload={reloadDetail}
            />
          </Suspense>
        ) : (
          <>
            {/* Two primary CTAs, side by side: pickup (load into van) + esita
                (assign delivery outcome). Both use the same scan UX (camera
                or laser) so the portalettere always interacts with the
                scanner first and never has to scroll the today-list to find
                a raccomandata. */}
            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={openPickupScanner}
                className="w-full rounded-3xl p-5 text-left bg-gradient-to-br from-[color:var(--brand-orange)] to-orange-600 text-white shadow-lg active:scale-[0.98] transition-transform"
                data-testid="m-pickup-button"
              >
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                    {scanMode === 'laser' ? <Zap className="h-6 w-6" /> : <QrCode className="h-6 w-6" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider opacity-90">Inizia turno consegne</div>
                    <div className="text-lg font-semibold leading-tight">Metti in consegna</div>
                    <div className="text-xs opacity-90 mt-0.5">
                      {scanMode === 'laser' ? 'Premi il grilletto del palmare per ogni raccomandata' : 'Scansiona i QR delle raccomandate da caricare in furgone'}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 opacity-80" />
                </div>
              </button>

              <button
                onClick={openLookupScanner}
                className="w-full rounded-3xl p-5 text-left bg-white border-2 border-[color:var(--brand-orange)] text-[color:var(--brand-orange-dark)] shadow-md active:scale-[0.98] transition-transform"
                data-testid="m-esita-button"
              >
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-orange-100 inline-flex items-center justify-center shrink-0">
                    {scanMode === 'laser' ? <Zap className="h-6 w-6 text-[color:var(--brand-orange)]" /> : <ScanLine className="h-6 w-6 text-[color:var(--brand-orange)]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--brand-orange)] opacity-90">Esita una consegna</div>
                    <div className="text-lg font-semibold leading-tight text-zinc-900">Esita raccomandata</div>
                    <div className="text-xs text-zinc-600 mt-0.5">
                      {scanMode === 'laser' ? 'Premi il grilletto sul QR della busta · poi scegli l\u2019esito' : 'Scansiona il QR sulla busta · poi scegli l\u2019esito'}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-[color:var(--brand-orange)]" />
                </div>
              </button>

              {/* Fine Giro — appears only when there is at least one item in
                  today's list. Opens the A5 PDF report in a new tab; the
                  operator prints it through the device's standard share /
                  print menu. Disabled until today list has data so we don't
                  generate empty reports by mistake. */}
              {today.length > 0 && (
                <button
                  onClick={closeRound}
                  disabled={fineGiroBusy}
                  className="w-full rounded-3xl p-4 text-left bg-zinc-900 text-white shadow-lg active:scale-[0.98] transition-transform disabled:opacity-60"
                  data-testid="m-fine-giro-button"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
                      {fineGiroBusy ? <Printer className="h-5 w-5 animate-pulse" /> : <Flag className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wider opacity-80">Termina turno</div>
                      <div className="text-base font-semibold leading-tight">Fine Giro · Report PDF</div>
                      <div className="text-[11px] opacity-80 mt-0.5">Apre il report in PDF da stampare o condividere</div>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 opacity-80" />
                  </div>
                </button>
              )}
            </div>

            {/* Today list (secondary: just for awareness; the operator
                normally exits via the "Esita raccomandata" CTA above) */}
            <Card className="p-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-[color:var(--hairline)] flex items-center justify-between">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <Truck className="h-4 w-4" /> Da consegnare oggi
                  {today.length > 0 && <span className="ml-1 text-[10px] font-bold bg-[color:var(--brand-orange)] text-white rounded-full px-1.5 py-0.5">{today.length}</span>}
                </div>
                <button onClick={loadToday} className="h-8 w-8 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center" data-testid="m-today-refresh"><RefreshCw className={`h-4 w-4 ${loadingToday ? 'animate-spin' : ''}`} /></button>
              </div>
              {/* Stale-while-revalidate: if we have cached data we show it
                  immediately and only the refresh icon spins. The full
                  "Caricamento…" placeholder is shown only when we have
                  literally nothing to display yet. */}
              {loadingToday && today.length === 0 ? (
                <div className="p-6 text-center text-sm text-[color:var(--text-secondary)]">Caricamento…</div>
              ) : today.length === 0 ? (
                <div className="p-6 text-center" data-testid="m-today-empty">
                  <Package className="h-10 w-10 mx-auto text-[color:var(--text-secondary)] mb-2" />
                  <div className="text-sm font-medium">Nessuna raccomandata in consegna</div>
                  <div className="text-xs text-[color:var(--text-secondary)] mt-1">Premi <span className="font-semibold text-[color:var(--brand-orange)]">Metti in consegna</span> e scansiona i QR per iniziare.</div>
                </div>
              ) : (
                <ul className="divide-y divide-[color:var(--hairline)]" data-testid="m-today-list">
                  {todayList}
                </ul>
              )}
            </Card>

            {/* Fallback: manual code entry. Kept tiny because in real use
                the scanner does 99% of the work; this is here only for the
                rare unreadable QR / scanner failure. */}
            <details className="rounded-2xl border border-[color:var(--hairline)] bg-white">
              <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-zinc-700 flex items-center gap-2 select-none" data-testid="m-manual-fallback-toggle">
                <SearchIcon className="h-4 w-4" /> QR illeggibile? Inserisci il codice manualmente
              </summary>
              <div className="px-4 pb-4 pt-1">
                <div className="flex gap-2">
                  <Input
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') lookup(code); }}
                    placeholder="Inserisci codice tracking\u2026"
                    testid="m-code-input"
                    className="flex-1 font-mono"
                    autoComplete="off"
                  />
                  <Btn variant="accent" onClick={() => lookup(code)} disabled={searching} testid="m-search-button"><SearchIcon className="h-4 w-4" /></Btn>
                </div>
              </div>
            </details>

            <div className="text-center text-[10px] text-[color:var(--text-secondary)] pt-2">
              Ottimizzato per smartphone e palmari Zebra · Solo le raccomandate prese in carico oggi compaiono in lista
            </div>
          </>
        )}
      </main>

      {/* Camera overlay (used when scanMode === 'camera') */}
      {scannerOpen && (
        <Suspense fallback={<div className="fixed inset-0 z-50 bg-zinc-900/95 flex items-center justify-center text-white text-sm">Caricamento scanner…</div>}>
          <QrScannerOverlay
            onResult={onScanResult}
            onClose={closeCameraScanner}
            continuous={scannerMode === 'pickup'}
            title={scannerMode === 'pickup' ? `Metti in consegna${pickupCount > 0 ? ` · ${pickupCount}` : ''}` : 'Esita raccomandata'}
            subtitle={scannerMode === 'pickup' ? 'Inquadra il QR di ogni raccomandata da caricare' : 'Inquadra il QR della busta per scegliere l\u2019esito'}
            feedback={scanFeedback}
          />
        </Suspense>
      )}

      {/* Laser session overlay (used when scanMode === 'laser') */}
      {laserSession && (
        <div className="fixed inset-0 z-50 bg-zinc-900/95 flex flex-col text-white" data-testid="m-laser-overlay">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2 min-w-0">
              <Zap className="h-5 w-5 text-[color:var(--brand-orange)]" />
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight truncate" data-testid="m-laser-title">
                  {laserSession === 'pickup' ? `Metti in consegna${pickupCount > 0 ? ` · ${pickupCount}` : ''}` : 'Esita raccomandata'}
                </div>
                <div className="text-[11px] opacity-70">Scanner hardware attivo</div>
              </div>
            </div>
            <button onClick={closeLaserSession} className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center" data-testid="m-laser-close">
              <span className="text-lg">×</span>
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-4">
            <div className="relative">
              <div className="h-24 w-24 rounded-full bg-[color:var(--brand-orange)]/20 flex items-center justify-center animate-pulse">
                <ScanLine className="h-12 w-12 text-[color:var(--brand-orange)]" />
              </div>
            </div>
            <div>
              <div className="text-lg font-semibold">Premi il grilletto del palmare</div>
              <div className="text-sm opacity-75 mt-1">Inquadra il QR / barcode della raccomandata.<br />Il sistema riceve la scansione automaticamente.</div>
            </div>
            {/* Diagnostic panel — ALWAYS visible while the laser overlay is
                open, even with an empty buffer. The operator must be able
                to tell at a glance whether DataWedge is delivering chars
                to the WebView. If this stays "in attesa…" forever after
                a successful "BIP" from the scanner, the DataWedge profile
                is misconfigured (Intent output instead of Keystroke, or
                the profile is not associated with Chrome/WebView). */}
            <div className="font-mono text-xs px-3 py-2 rounded-xl bg-white/10 text-white/95 break-all max-w-full min-h-[34px]" data-testid="m-laser-buffer">
              {laserBuffer ? (
                <>
                  <span className="opacity-60">Letto: </span>
                  <span className="font-semibold">{laserBuffer.slice(-40)}</span>
                  <span className="ml-2 opacity-50">({laserBuffer.length})</span>
                </>
              ) : (
                <span className="opacity-60">In attesa di scansione…</span>
              )}
            </div>
            {/* Hidden capture input: DataWedge keystroke mode injects scanned
                chars into the focused element, so we keep focus on this off-
                screen input. The window-level keydown listener in
                useHardwareScanner mirrors the events for parsing — we don't
                read from this input directly. Both layers running together
                make the scan capture robust to focus shifts on the TC27
                Android WebView. */}
            <input
              ref={laserInputRef}
              type="text"
              inputMode="none"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              aria-hidden="true"
              tabIndex={-1}
              defaultValue=""
              onFocus={() => setLaserInputFocused(true)}
              onChange={(e) => {
                // Fallback parser for DataWedge variants that deliver the
                // whole barcode in a single input event (no per-key keydown).
                // We try to interpret the value as a scan; if it matches, we
                // fire onHardwareScan and clear the field. The window-level
                // keydown listener in useHardwareScanner still runs in
                // parallel — whichever fires first wins, and the other
                // gets a no-op (buffer already cleared).
                const v = e.target.value || '';
                // Common scanner suffix is CR / LF / Tab → strong signal.
                const looksTerminated = /[\r\n\t]/.test(v);
                const code = parseScannerInput(v);
                if (code && (looksTerminated || v.length >= 6)) {
                  e.target.value = '';
                  setLaserBuffer('');
                  if (onHardwareScan) onHardwareScan(code);
                }
              }}
              onBlur={() => {
                setLaserInputFocused(false);
                setTimeout(() => { try { laserInputRef.current?.focus({ preventScroll: true }); } catch (err) { /* ignore */ } }, 50);
              }}
              className="absolute opacity-0 pointer-events-none -z-10"
              style={{ left: -9999, top: -9999, width: 1, height: 1 }}
              data-testid="m-laser-capture-input"
            />
            {/* Manual focus recovery: if the WebView dropped focus from the
                hidden input (e.g. user tapped a notification, the app went
                to background and came back) DataWedge keystrokes would go
                nowhere. Tapping this big button re-grabs focus instantly. */}
            {!laserInputFocused && (
              <button
                type="button"
                onClick={() => { try { laserInputRef.current?.focus({ preventScroll: true }); } catch (err) { /* ignore */ } }}
                className="w-full max-w-xs rounded-2xl bg-[color:var(--brand-orange)] text-white font-semibold py-3 px-4 shadow-lg active:scale-[0.98] transition-transform"
                data-testid="m-laser-refocus"
              >
                Tocca per attivare lo scanner
              </button>
            )}
            {laserSession === 'pickup' && pickupCount > 0 && (
              <div className="text-xs px-3 py-1.5 rounded-full bg-white/10">
                {pickupCount} raccomandat{pickupCount === 1 ? 'a presa' : 'e prese'} in consegna in questa sessione
              </div>
            )}
          </div>
          {laserFeedback && (
            <div className={`mx-4 mb-4 rounded-2xl px-4 py-3 shadow-xl ${laserFeedback.tone === 'success' ? 'bg-emerald-500 text-white' : laserFeedback.tone === 'error' ? 'bg-rose-500 text-white' : 'bg-white text-zinc-900'} animate-in fade-in slide-in-from-bottom-2`} data-testid="m-laser-feedback">
              <div className="text-sm font-semibold leading-tight">{laserFeedback.title}</div>
              {laserFeedback.message && <div className="text-xs opacity-95 mt-0.5">{laserFeedback.message}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
